import { Component, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import { OctagonAlert } from "lucide-react";
import { localeAtom } from "../stores/locale";
import { useT } from "../lib/i18n";
import { captureException } from "../lib/sentry";
import { isChunkLoadError, reloadOnceForStaleChunk } from "../lib/chunkReload";

// ErrorBoundary is the single top-level boundary for the app (wired in
// main.tsx). It exists so an uncaught render error degrades to a retry-able
// surface instead of a blank white screen — CLAUDE.md FE principle #4
// ("errors are structured, fallbacks are retry-able").
//
// It catches only errors that propagate out of render — feature-local error
// handling (api/client error states, auth store, etc.) is unaffected, so the
// happy-path stays exactly as it was. React error boundaries must be class
// components; the visible fallback is a separate function component so it can
// use hooks (i18n / locale).
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Final net for a stale dynamic-import that didn't go through lazyWithReload
    // (e.g. a non-route chunk): if a deploy rotated the chunk out from under a
    // pre-deploy tab, reload once to the fresh build rather than stranding the
    // user on the error screen. The session guard prevents a reload loop.
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) return;
    // console.error so PostHog/Datadog pick it up (console.warn is not
    // reliably captured). Worker manifest W-07's "no silent fail" applied to
    // the client: an uncaught render error must leave a trace.
    console.error("[ErrorBoundary] uncaught render error", error, info?.componentStack);
    // Forward render errors to Sentry. No-op when Sentry is unconfigured (no
    // DSN), so local dev is unchanged. Sentry.init also auto-captures unhandled
    // errors; this is the render-error path that a boundary swallows.
    captureException(error);
  }

  private handleRetry = () => {
    // Reset the boundary so the subtree re-mounts and re-renders. If the
    // error is persistent the boundary simply trips again — that is the
    // intended retry-able behaviour, not a loop we need to guard.
    this.setState({ error: null });
  };

  render() {
    if (this.state.error != null) {
      return <ErrorFallback onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({ onRetry }: { onRetry: () => void }) {
  const locale = useAtomValue(localeAtom);
  const t = useT(locale);

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-2xl">
      <div
        className="flex max-w-[28rem] flex-col items-center gap-lg px-3xl py-4xl text-center"
        style={{
          border: "1px solid var(--border)",
          borderRadius: "12px",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <span style={{ color: "var(--gold)", opacity: 0.9 }}>
          <OctagonAlert size={28} strokeWidth={1.5} />
        </span>
        <div className="flex flex-col gap-sm">
          <p
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "var(--text-strong)",
            }}
          >
            {t("errorBoundary.title")}
          </p>
        </div>
        <p
          className="text-sm leading-relaxed"
          style={{ color: "var(--text-muted)" }}
        >
          {t("errorBoundary.body")}
        </p>
        <div className="flex items-center gap-md">
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md px-lg py-sm text-sm font-medium"
            style={{
              border: "1px solid var(--gold)",
              color: "var(--gold)",
              background: "transparent",
            }}
          >
            {t("errorBoundary.retry")}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md px-lg py-sm text-sm font-medium"
            style={{ color: "var(--text-muted)", background: "transparent" }}
          >
            {t("errorBoundary.reload")}
          </button>
        </div>
      </div>
    </div>
  );
}
